package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initParrot_in_the_browserPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("parrot_in_the_browser.md", FrontMatterElement.Builder { addScalar("title", "My Android app in a browser: Parrot on Kotlin/Wasm"); addScalar("description", "How Parrot's shared Kotlin code went from phones to the browser: 57 of 59 modules compiling with two build files changed, a SQLite database saved into IndexedDB, Readium under a Compose canvas, and what the first screen costs to download."); addScalar("date", "2026-10-16"); addScalar("cover", "/articles/kotlin_wasm_browser.jpeg"); }))
}

@Page("/blog/android-app-in-the-browser-kotlin-wasm")
@Layout(".pages.MarkdownPage")
@Composable
fun Parrot_in_the_browserPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("my-android-app-in-a-browser-parrot-on-kotlin-wasm") }) {
        org.jetbrains.compose.web.dom.Text("My Android app in a browser: Parrot on Kotlin/Wasm")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot is an ebook and audiobook reader for Android and iPhone. Almost all of it is shared Kotlin: the database, sync, the reader logic and every screen in Compose Multiplatform. Compose also runs in a browser through Kotlin/Wasm, so I wanted to know how much of Parrot would follow it there, and how much existing code would have to change.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first answer came the same day. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("57 of the 59 shared modules compiled for the browser, the reader and every screen module included, and the only existing files that changed were two Gradle files.")
        }
        org.jetbrains.compose.web.dom.Text(" Getting from \"compiles\" to \"you can read a book in it\" took a lot longer, and that's what this note is about.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-flag-no-module-changes") }) {
        org.jetbrains.compose.web.dom.Text("One flag, no module changes")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I didn't want the web experiment to touch the Android or iPhone builds at all. So the browser target is added from the root build file, only when Gradle is run with ")
        io.github.retar.portfolio.components.widgets.InlineCode("""-Pweb""")
        org.jetbrains.compose.web.dom.Text(":")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""// Web spike: -Pweb adds a browser target to every multiplatform module, so no module's own
// build file changes and Android and iPhone builds never see it.
if (providers.gradleProperty("web").isPresent) {
    // Their shared code needs Firebase, which has no browser build; the web app wires its own.
    val notOnWeb = setOf(":composeApp", ":lib:analytics:implementation")
    ...
    subprojects {
        pluginManager.withPlugin("org.jetbrains.kotlin.multiplatform") {
            extensions.configure<KotlinMultiplatformExtension> {
                wasmJs { browser() }
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Without the flag nothing changes, and the Android test suite (3,513 tests at the time) ran the same before and after.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The two modules left out explain themselves. The analytics module's shared code uses Firebase, which has no browser build, and the app's root module depends on it. The web app got its own analytics module instead, reusing seven of the nine shared files and replacing the two that touch Firebase.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("shims-for-what-a-browser-doesn-t-have") }) {
        org.jetbrains.compose.web.dom.Text("Shims for what a browser doesn't have")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Shared code that compiles for Android and iOS still assumes a few things a browser lacks. Four of them came up: ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Dispatchers.IO""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""runBlocking""")
        org.jetbrains.compose.web.dom.Text(", ")
        io.github.retar.portfolio.components.widgets.InlineCode("""FileSystem.SYSTEM""")
        org.jetbrains.compose.web.dom.Text(" from Okio, and ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Map.putIfAbsent""")
        org.jetbrains.compose.web.dom.Text(". A small browser-only module provides stand-ins, so the shared code resolves them without being edited:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""// shortcut: kotlinx.coroutines has no Dispatchers.IO for the browser (one thread), so shared code's
// `import kotlinx.coroutines.IO` resolves here. Delete if the library adds its own.
val Dispatchers.IO: CoroutineDispatcher get() = Dispatchers.Default

// shortcut: a browser cannot block. This only finishes work that never really waits; the four shared
// callers need a real change (make them suspend) before the web app can rely on them.
fun <T> runBlocking(
    context: CoroutineContext = EmptyCoroutineContext,
    block: suspend CoroutineScope.() -> T,
): T {
    var result: Result<T>? = null
    block.startCoroutine(CoroutineScope(context), Continuation(context) { result = it })
    return (result ?: error("runBlocking cannot wait in the browser")).getOrThrow()
}
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One detail cost time: these stand-ins have to live in a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("separate")
        }
        org.jetbrains.compose.web.dom.Text(" module. Declared inside the same module as the shared code that uses them, they're invisible to it. As a dependency of every module, they work.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The biggest saving came from the iPhone port. On iOS, the reader is drawn by native code behind a Kotlin interface: 46 functions that the Swift side implements. Nineteen iPhone source files (1,476 lines), including the reader controller, the audio controller and the download manager, compiled for the browser unchanged, and the bridge interface needed one type changed. The web reader implements the same bridge that Swift does.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-compiles-doesn-t-tell-you") }) {
        org.jetbrains.compose.web.dom.Text("What \"it compiles\" doesn't tell you")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Getting the real app to start showed the problems the compiler couldn't see:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Five services registered only in platform folders")
            }
            org.jetbrains.compose.web.dom.Text(": file import, EPUB metadata, the transfer file store, catalogue staging files and font import. The phones register them in Android or iOS code, so nothing in shared code says a browser needs them. Each one showed up as a crash at start.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Two classes with the same short name.")
            }
            org.jetbrains.compose.web.dom.Text(" On the web, the dependency injection library told classes apart by their short name, so the settings module's ")
            io.github.retar.portfolio.components.widgets.InlineCode("""LogoutUseCase""")
            org.jetbrains.compose.web.dom.Text(" clashed with the auth module's. Renaming one was the first edit to shared code, and it's the kind of thing a real port needs a check for.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("By the end of that first day, the app's own root started in a browser, the home screen and Settings opened, and a sample book could be opened and paged through.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-database-without-blocking-calls") }) {
        org.jetbrains.compose.web.dom.Text("A database without blocking calls")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot's database is SQLDelight on SQLite. The browser drivers for SQLDelight are asynchronous, and the app isn't. I measured what switching would mean: turning on SQLDelight's asynchronous mode gave 43 compile errors in 10 files in the database module alone, plus 267 blocking read calls that would each need to change.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the web app runs SQLite synchronously in the page instead, through ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/sql-js/sql.js") {
            org.jetbrains.compose.web.dom.Text("sql.js")
        }
        org.jetbrains.compose.web.dom.Text(", SQLite compiled to WebAssembly. The app's blocking calls work as they are. The cost is persistence: sql.js keeps the database in memory, so the web app saves the whole database into IndexedDB as bytes:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("one second after a write, or after the outermost transaction ends (a burst of writes is one save),")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("at once when the page is hidden or closed,")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("never inside a transaction.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In the persistence test the database was 573,440 bytes, and a save took about 2 ms. A page turned 0.15 seconds before a reload was kept.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The web database opens through the same shared code as the phones, with the same rules for migrating an old version and setting aside one it can't read. I tested the second case by rewriting the saved bytes to claim version 99. The app logged \"Saved database is version 99, the app is at 45\", kept the old bytes under a recovery key and started a fresh database, the same as a phone would.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-reader-an-html-page-under-a-compose-canvas") }) {
        org.jetbrains.compose.web.dom.Text("The reader: an HTML page under a Compose canvas")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Readium, the EPUB engine Parrot uses on the phones, has a web navigator. The web reader hosts it behind the same 46-function bridge. Wiring the basics (open, turn pages, go to a chapter, report positions, taps) was quick. Drawing Compose on top of it wasn't.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the web, Compose draws to a canvas, and an embedded HTML element is always drawn ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("above")
        }
        org.jetbrains.compose.web.dom.Text(" that canvas and takes every click in its area. On the phones, the reader's bars, sheets and dialogs are drawn over the page. In the browser, they'd sit hidden behind it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The first fix cut holes in the page wherever a bar was. It worked, but every floating thing in shared code would have had to report where it was. The second approach copied iOS: put the page ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("under")
        }
        org.jetbrains.compose.web.dom.Text(" the canvas and clear the canvas where the page should show through. Everything Compose draws lands on top, with nothing handled per element.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That broke text selection and links, because the page no longer got any clicks. The version Parrot uses now lets presses fall through the canvas to the page, then copies each press back to Compose first. If a Compose control is under the pointer, Compose takes the whole gesture. Otherwise the page keeps it, and selection, links and long presses on pictures work natively.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("It needed no change to shared code. The cost is that it relies on Compose internals in three places: how its HTML layer and canvas are nested, which event fields it reads, and that it handles a press before the event call returns. A Compose update can break any of them, and the code says so where it does it.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-the-browser-downloads") }) {
        org.jetbrains.compose.web.dom.Text("What the browser downloads")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the release build served with Brotli compression, the first screen is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("6.9 MB")
        }
        org.jetbrains.compose.web.dom.Text(" across 27 files. Almost all of that is two WebAssembly files: the app (3.0 MB) and Skia, the graphics engine Compose draws with (2.6 MB). On a throttled 10 Mbit/s connection, that's 5 seconds to the first screen, 4.5 of them spent on the two ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".wasm""")
        org.jetbrains.compose.web.dom.Text(" files. A second visit takes about 0.25 seconds.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Without compression on the server, the same first screen is about 27 MB. Compression is the single biggest thing in the whole setup.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("After the first screen, a service worker downloads the rest of the build in the background (17 MB compressed, including a 22 MB English dictionary that compresses to 3.7 MB). From then on, the app starts and reads the books already in the browser with no network, and the browser offers to install it.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("honest-status") }) {
        org.jetbrains.compose.web.dom.Text("Honest status")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The web version is merged into the main codebase and being prepared to go live. Some things are still open:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Your own servers.")
            }
            org.jetbrains.compose.web.dom.Text(" A web page may only read another server's answer if that server allows it, and self-hosted book servers like Storyteller and Audiobookshelf don't. The phones have no such rule. Parrot Cloud works in the browser; your own servers need a browser extension, which is a story for another note.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Browsers.")
            }
            org.jetbrains.compose.web.dom.Text(" Most checks ran in Brave and Chrome. Safari and Firefox have been tested for the book-safety checks, not for everything.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Fragility.")
            }
            org.jetbrains.compose.web.dom.Text(" The reader's layering depends on Compose internals, and the in-page SQLite database depends on saving the whole file. Both are fine at today's sizes and both are marked as shortcuts in the code.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The thing I didn't expect: the hardest parts weren't Kotlin at all. They were the browser's own rules about blocking, layering and who may read what, and none of them showed up until something ran.")
    }
}
